import { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { ArrowDown, ArrowUp, Plus, RotateCcw, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MAX_PINNED_QUICK_ACTIONS } from "./quick-actions.data";
import type { IQuickAction } from "./types";

interface ICustomizeQuickActionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Everything the user may pin, already permission-filtered. */
  available: IQuickAction[];
  /** The bar as it stands — the dialog's starting draft. */
  pinned: IQuickAction[];
  isCustomized: boolean;
  isSaving: boolean;
  onSave: (keys: string[]) => Promise<unknown>;
  onReset: () => Promise<unknown>;
}

const ROW_CLASS_NAME =
  "flex items-center gap-2.5 rounded-sm border border-border bg-surface px-2.5 py-1.5";

/**
 * Pick and order the bar's shortcuts.
 *
 * Edits a local draft and writes once on Save, rather than on every click like
 * the canvas does — the bar is a handful of buttons, so there is nothing to
 * gain from live-saving and a half-made choice should not flash onto the page
 * behind the dialog. Up/down buttons instead of drag: the list is short, and
 * they work the same with a keyboard, a screen reader and a finger.
 */
export const CustomizeQuickActionsDialog = ({
  open,
  onOpenChange,
  available,
  pinned,
  isCustomized,
  isSaving,
  onSave,
  onReset,
}: ICustomizeQuickActionsDialogProps) => {
  const { t } = useTranslation(["company", "common"]);
  const [draft, setDraft] = useState<IQuickAction[]>(pinned);

  const draftKeys = new Set(draft.map((entry) => entry.key));
  const unpinned = available.filter((entry) => !draftKeys.has(entry.key));
  const isFull = draft.length >= MAX_PINNED_QUICK_ACTIONS;

  const move = (index: number, by: -1 | 1) =>
    setDraft((current) => {
      const next = [...current];
      [next[index], next[index + by]] = [next[index + by], next[index]];
      return next;
    });

  const handleSave = async () => {
    try {
      await onSave(draft.map((entry) => entry.key));
      toast.success(t("company:dashboard_quick_actions.saved"));
      onOpenChange(false);
    } catch {
      toast.error(t("company:dashboard_quick_actions.save_failed"));
    }
  };

  const handleReset = async () => {
    try {
      await onReset();
      toast.success(t("company:dashboard_quick_actions.reset_done"));
      onOpenChange(false);
    } catch {
      toast.error(t("company:dashboard_quick_actions.reset_failed"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {t("company:dashboard_quick_actions.customize_title")}
          </DialogTitle>
          <DialogDescription>
            {t("company:dashboard_quick_actions.customize_description", {
              max: MAX_PINNED_QUICK_ACTIONS,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto">
          <section className="flex flex-col gap-1.5">
            <h3 className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
              {t("company:dashboard_quick_actions.on_your_bar")}
              <span className="tabular-nums">
                {draft.length}/{MAX_PINNED_QUICK_ACTIONS}
              </span>
            </h3>
            {draft.length === 0 ? (
              <p className="rounded-sm border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
                {t("company:dashboard_quick_actions.none_pinned_hint")}
              </p>
            ) : (
              <ol className="flex flex-col gap-1">
                {draft.map((entry, index) => {
                  const Icon = entry.icon;
                  const label = t(entry.label);
                  return (
                    <li key={entry.key} className={ROW_CLASS_NAME}>
                      <span className="grid size-6 shrink-0 place-items-center rounded-sm bg-primary-muted text-primary">
                        <Icon size={12} aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                        {label}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        disabled={index === 0}
                        onClick={() => move(index, -1)}
                        aria-label={t("company:dashboard_quick_actions.move_up", { label })}
                      >
                        <ArrowUp className="size-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        disabled={index === draft.length - 1}
                        onClick={() => move(index, 1)}
                        aria-label={t("company:dashboard_quick_actions.move_down", { label })}
                      >
                        <ArrowDown className="size-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        onClick={() =>
                          setDraft((current) =>
                            current.filter((item) => item.key !== entry.key),
                          )
                        }
                        aria-label={t("company:dashboard_quick_actions.remove", { label })}
                      >
                        <X className="size-3.5" />
                      </Button>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>

          {unpinned.length > 0 && (
            <section className="flex flex-col gap-1.5">
              <h3 className="text-xs font-semibold text-muted-foreground">
                {t("company:dashboard_quick_actions.more_actions")}
              </h3>
              <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                {unpinned.map((entry) => {
                  const Icon = entry.icon;
                  const label = t(entry.label);
                  return (
                    <li key={entry.key} className={ROW_CLASS_NAME}>
                      <span className="grid size-6 shrink-0 place-items-center rounded-sm bg-muted text-muted-foreground">
                        <Icon size={12} aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                        {label}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        disabled={isFull}
                        onClick={() => setDraft((current) => [...current, entry])}
                        aria-label={t("company:dashboard_quick_actions.add", { label })}
                      >
                        <Plus className="size-3.5" />
                      </Button>
                    </li>
                  );
                })}
              </ul>
              {isFull && (
                <p className="text-xs text-muted-foreground">
                  {t("company:dashboard_quick_actions.limit_reached", {
                    max: MAX_PINNED_QUICK_ACTIONS,
                  })}
                </p>
              )}
            </section>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {isCustomized ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={isSaving}
              onClick={handleReset}
            >
              <RotateCcw className="size-3.5" />
              {t("company:dashboard_quick_actions.reset_to_default")}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={isSaving}
              onClick={() => onOpenChange(false)}
            >
              {t("common:buttons.cancel")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              isLoading={isSaving}
              onClick={handleSave}
            >
              {t("common:buttons.save")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
